import { useCallback, useRef, useState } from 'react';
import {
  VIDEO_EDITOR_HISTORY_MAX,
  cloneEditorSnapshot,
  snapshotsEqual,
} from '@/lib/videoEditorHistory';

/**
 * Undo/redo stack for video editor timeline state.
 * Call `pushHistory` immediately before a user edit (drag start, delete, paste, etc.).
 */
export function useVideoEditorUndoRedo({
  getSnapshot,
  applySnapshot,
  maxHistory = VIDEO_EDITOR_HISTORY_MAX,
}) {
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const restoringRef = useRef(false);
  const [revision, setRevision] = useState(0);

  const bump = useCallback(() => setRevision((r) => r + 1), []);

  const pushHistory = useCallback(() => {
    if (restoringRef.current) return;
    const snap = getSnapshot();
    if (!snap) return;
    const cloned = cloneEditorSnapshot(snap);
    const past = pastRef.current;
    const last = past[past.length - 1];
    if (last && snapshotsEqual(last, cloned)) return;
    pastRef.current = [...past, cloned].slice(-maxHistory);
    futureRef.current = [];
    bump();
  }, [getSnapshot, maxHistory, bump]);

  const undo = useCallback(() => {
    const past = pastRef.current;
    if (!past.length) return false;
    const previous = past[past.length - 1];
    const current = getSnapshot();
    restoringRef.current = true;
    try {
      futureRef.current = [cloneEditorSnapshot(current), ...futureRef.current].slice(0, maxHistory);
      pastRef.current = past.slice(0, -1);
      applySnapshot(cloneEditorSnapshot(previous));
    } finally {
      restoringRef.current = false;
      bump();
    }
    return true;
  }, [getSnapshot, applySnapshot, maxHistory, bump]);

  const redo = useCallback(() => {
    const future = futureRef.current;
    if (!future.length) return false;
    const next = future[0];
    const current = getSnapshot();
    restoringRef.current = true;
    try {
      pastRef.current = [...pastRef.current, cloneEditorSnapshot(current)].slice(-maxHistory);
      futureRef.current = future.slice(1);
      applySnapshot(cloneEditorSnapshot(next));
    } finally {
      restoringRef.current = false;
      bump();
    }
    return true;
  }, [getSnapshot, applySnapshot, maxHistory, bump]);

  const resetHistory = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    bump();
  }, [bump]);

  const canUndo = revision >= 0 && pastRef.current.length > 0;
  const canRedo = revision >= 0 && futureRef.current.length > 0;

  return {
    pushHistory,
    undo,
    redo,
    resetHistory,
    canUndo,
    canRedo,
  };
}
